/**
 * "Reference Information" of the RMBL page, like the linked HFL references of the OMBL page:
 * one card per linked RHFL with each of its trucks. Plate, trailer, driver, CMR and seal can be
 * corrected here; the RMBL page writes those corrections back to the RHFL when it saves. On an
 * LTL RMBL each RHFL also picks the linehaul truck that carries it.
 */
import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TextField from "components/BufferedTextField";
import { truckLabel, truckLoadTotals, normalizeCargoList } from "./roadTruckModel";

const EDITABLE_FIELDS = [
    ["plate", "Plate Number"],
    ["trailerPlate", "Trailer Plate"],
    ["driverName", "Driver Name"],
    ["driverPhone", "Driver Phone"],
    ["cmrNumber", "CMR Number"],
    ["sealNumber", "Seal Number"],
];

// The page field style reserves room for an end adornment, which cuts these short values.
const truckFieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" },
};

const fmt = (value, decimals = 0) =>
    (Number(value) || 0).toLocaleString("en-US", { maximumFractionDigits: decimals });

const dateText = (value) => (value ? new Date(value).toLocaleDateString("tr-TR") : "-");

const place = (location) => [location?.city, location?.country].filter(Boolean).join(", ") || "-";

const fact = (label, value) => (
    <Grid item xs={6} lg={3}>
        <MDTypography variant="caption" color="text">
            {label}
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold" display="block">
            {value || "-"}
        </MDTypography>
    </Grid>
);

/**
 * rows: [{ id, attributes, trucks, edited }] — trucks already include unsaved corrections,
 * edited is true while there are some.
 */
export default function RmblReferences({
    rmblNumber,
    onOpenRmbl,
    rows,
    loading,
    onEditTruck,
    onOpenShipment,
    linehaulTrucks,
    truckAssignments,
    onAssign,
    fieldSx,
    linkSx,
}) {
    return (
        <MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                        Reference Information
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                        Linked RHFL shipment and truck references
                    </MDTypography>
                </MDBox>
                <Chip size="small" label={`${rows.length} RHFL`} sx={{ fontWeight: "bold" }} />
            </MDBox>

            <TextField
                label="RMBL Number"
                value={rmblNumber || ""}
                fullWidth
                size="small"
                sx={{ ...fieldSx, mb: 1.5 }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true, onClick: onOpenRmbl, style: { cursor: onOpenRmbl ? "pointer" : "default" } }}
            />

            {loading && (
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Loading the current RHFL trucks...
                </MDTypography>
            )}

            {rows.map((row) => {
                const a = row.attributes || {};
                const cargo = normalizeCargoList(a.cargoDetails || [], row.trucks);
                return (
                    <Card
                        key={row.id}
                        data-testid="rmbl-rhfl-card"
                        sx={{
                            p: 1.75,
                            mb: 1.5,
                            borderRadius: "14px",
                            border: row.edited ? "1px solid #f59e0b" : "1px solid #e2e8f0",
                            backgroundColor: "#fff",
                            boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                        }}
                    >
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color="info"
                                sx={linkSx}
                                onClick={() => onOpenShipment(row.id)}
                            >
                                RHFL{a.shipmentNo || ""}
                            </MDTypography>
                            <MDBox display="flex" gap={0.5} flexWrap="wrap">
                                {row.edited && (
                                    <Chip size="small" label="Unsaved truck changes" sx={{ color: "#b45309", backgroundColor: "#fffbeb" }} />
                                )}
                                <Chip size="small" label={a.roadTransportType || "Road"} sx={{ fontWeight: "bold" }} />
                                <Chip size="small" label={`${row.trucks.length} truck${row.trucks.length === 1 ? "" : "s"}`} />
                            </MDBox>
                        </MDBox>

                        <Grid container spacing={1}>
                            {fact("Customer", a.customer)}
                            {fact("Route", `${place(a.pickupLocation)} → ${place(a.deliveryLocation)}`)}
                            {fact("Pickup", dateText(a.pickupDate))}
                            {fact("ETA", dateText(a.estimatedDeliveryDate))}
                        </Grid>

                        {linehaulTrucks?.length > 0 && (
                            <TextField
                                select
                                label="Linehaul Truck"
                                value={truckAssignments?.[row.id] || linehaulTrucks[0].truckId}
                                onChange={(e) => onAssign(row.id, e.target.value)}
                                fullWidth
                                size="small"
                                sx={{ ...fieldSx, mt: 1.5 }}
                                InputLabelProps={{ shrink: true }}
                            >
                                {linehaulTrucks.map((truck, i) => (
                                    <MenuItem key={truck.truckId} value={truck.truckId}>
                                        {truckLabel(truck, i)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mt={1.5}>
                            {linehaulTrucks?.length ? "Pickup trucks of the RHFL" : "Trucks"} ({row.trucks.length})
                        </MDTypography>

                        {row.trucks.map((truck, index) => {
                            const load = truckLoadTotals(cargo, row.trucks, truck.truckId);
                            return (
                                <MDBox
                                    key={truck.truckId}
                                    data-testid="rmbl-rhfl-truck"
                                    mt={1}
                                    p={1.25}
                                    sx={{ borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}
                                >
                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                                        <MDTypography variant="caption" fontWeight="bold">
                                            Truck {index + 1}
                                            {truck.truckType ? ` · ${truck.truckType}` : ""}
                                            {truck.carrier ? ` · ${truck.carrier}` : ""}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="text">
                                            {fmt(load.unitQuantity)} units · {fmt(load.grossWeight, 3)} kg · {fmt(load.ldm, 3)} LDM
                                        </MDTypography>
                                    </MDBox>
                                    <Grid container spacing={1}>
                                        {EDITABLE_FIELDS.map(([field, label]) => (
                                            <Grid item xs={12} sm={6} md={4} key={field}>
                                                <TextField
                                                    label={label}
                                                    value={truck[field] || ""}
                                                    onChange={(e) => onEditTruck(row.id, truck.truckId, field, e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    sx={truckFieldSx}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </MDBox>
                            );
                        })}
                    </Card>
                );
            })}
        </MDBox>
    );
}
